import React, { useCallback, useEffect, useState } from 'react';
import _ from 'lodash';
import { Modal, ModalVariant, Flex, FlexItem } from '@patternfly/react-core';

import { useTranslation } from 'localization/useTranslation';
import { format } from 'utils/string';
import { deleteButtons } from 'components/Modals/ModalButtons';
import useApiCall from 'services/useApiCall';
import useAPI from 'services/useAPI';
/**
 * @typedef {Object} ConfirmDeleteModalProps
 * @prop {boolean} [isLoading=false]
 * @prop {function} onConfirm
 * @prop {function} onCancel
 * @prop {object} [item] If set, the modal will be shown
 * @prop {string} itemName
 * @prop {React.ReactNode} [children]
 */

/**
 * Show a modal to confirm deletion of an item
 * @param {ConfirmDeleteModalProps} props
 * @returns {JSX.Element}
 */
const ConfirmDeleteModal = ({ isLoading, onConfirm, item, onCancel, itemName, children }) => {
  const T = useTranslation();
  const api = useAPI();
  const [deleteEnabled, setDeleteEnabled] = useState(false);
  const [numAssets, setNumAssets] = useState(0);

  const [getAssetsWithModel, isFetchingAssets] = useApiCall({
    apiCall: api.core.getAssetsByModel,
    onSuccess: useCallback((assets) => {
      if (_.isEmpty(assets)) {
        setDeleteEnabled(true);
        return;
      }
      setNumAssets(_.size(assets));
      setDeleteEnabled(false);
    }, []),
    onError: useCallback((error) => {
      console.error(error);
    }, [])
  });

  useEffect(function fetchingAssts() {
    if (item?.draft === false) {
      getAssetsWithModel(_.first(_.split(item?.id, '.')));
    } else {
      setDeleteEnabled(true);
    }
  }, [getAssetsWithModel, item, item?.id]);

  const _onCancel = useCallback(() => {
    setNumAssets(0);
    onCancel();
  }, [onCancel]);

  const _onConfirm = useCallback(() => {
    onConfirm(item);
  }, [onConfirm, item]);

  const isOpen = item != null;
  return (
    <Modal
      variant={ModalVariant.small}
      title={numAssets === 0 ? T.modals.confirmdelete.title : T.modals.confirmdelete.titleError}
      titleIconVariant={numAssets === 0 ? 'warning' : 'danger'}
      isOpen={isOpen}
      onClose={isLoading ? null : _onCancel}
      actions={deleteButtons({ T, onDelete: _onConfirm, deleteEnabled: deleteEnabled, onCancel: _onCancel, isLoading: (isLoading || isFetchingAssets) })}
    >
      <Flex>
        <FlexItem>
          { numAssets === 0 ? format(T.modals.confirmdelete.content, itemName) : format(T.modals.confirmdelete.tooManyAssets, numAssets) }
        </FlexItem>
        <FlexItem>
          { children }
        </FlexItem>
      </Flex>
    </Modal>
  );
};

export default React.memo(ConfirmDeleteModal);
